<template>
    <h1>Registrieren</h1>
    <p>
        Prüfen Sie Ihre persönlichen Daten und schließen Sie die Registrierung ab.
    </p>
    <div v-if="institutionName">
        <p class="subheadline">Einrichtung</p>
        <span class="is-block">{{ institutionName }}</span>
    </div>
    <div v-if="showChild">
        <p class="subheadline">Kind</p>
        <span class="is-block" v-if="registrationData.child.firstName || registrationData.child.lastName">
            {{ registrationData.child.firstName }} {{ registrationData.child.lastName }}
        </span>
        <span class="is-block" v-if="registrationData.child.dateOfBirth">
            Geburtsdatum: {{ (new Date(registrationData.child.dateOfBirth)).toLocaleDateString('de-DE') }}
        </span>
        <span class="is-block" v-if="registrationData.child.group">{{ registrationData.child.group }}</span>
        <span class="is-block" v-if="formattedChildDateStart">Teilnahme an der Essensversorung ab {{ formattedChildDateStart }}</span>
    </div>
    <div v-if="showParticipant">
        <p class="subheadline">Personen-Daten</p>
        <span class="is-block" v-if="registrationData.participant.firstName || registrationData.participant.lastName">
            {{ registrationData.participant.firstName }} {{ registrationData.participant.lastName }}
        </span>
        <br>
        <span class="is-block" v-if="registrationData.participant.phone">{{ registrationData.participant.phone }}</span>
        <span class="is-block" v-if="registrationData.participant.email">{{ registrationData.participant.email }}</span>
        <span class="is-block" v-if="registrationData.participant.dateOfBirth">
            Geburtsdatum: {{ (new Date(registrationData.participant.dateOfBirth)).toLocaleDateString('de-DE') }}
        </span>
    </div>
    <div v-if="showDeliveryAddress">
        <p class="subheadline">Lieferanschrift</p>
        <span class="is-block" v-if="registrationData.deliveryAddress.company">{{ registrationData.deliveryAddress.company }}</span>
        <span class="is-block" v-if="registrationData.deliveryAddress.firstName || registrationData.deliveryAddress.lastName">
            {{ registrationData.deliveryAddress.firstName }} {{ registrationData.deliveryAddress.lastName }}
        </span>
        <span class="is-block" v-if="registrationData.deliveryAddress.street">{{ registrationData.deliveryAddress.street }}</span>
        <span class="is-block" v-if="registrationData.deliveryAddress.zipCode || registrationData.deliveryAddress.city">
            {{ registrationData.deliveryAddress.zipCode }} {{ registrationData.deliveryAddress.city }}
        </span>
        <span class="is-block" v-if="registrationData.deliveryAddress.deliveryInfo">
            <br>
            {{ registrationData.deliveryAddress.deliveryInfo }}
        </span>
        <br>
        <span class="is-block" v-if="registrationData.deliveryAddress.phone">{{ registrationData.deliveryAddress.phone }}</span>
        <span class="is-block" v-if="registrationData.deliveryAddress.email">{{ registrationData.deliveryAddress.email }}</span>
        <span class="is-block" v-if="registrationData.deliveryAddress.dateOfBirth">
            Geburtsdatum: {{ (new Date(registrationData.deliveryAddress.dateOfBirth)).toLocaleDateString('de-DE') }}
        </span>
    </div>
    <div v-if="showBillingAddress">
        <p class="subheadline">Rechnungsanschrift</p>
        <span class="is-block" v-if="registrationData.billingAddress.company">{{ registrationData.billingAddress.company }}</span>
        <span class="is-block" v-if="registrationData.billingAddress.firstName || registrationData.billingAddress.lastName">
            {{ registrationData.billingAddress.firstName }} {{ registrationData.billingAddress.lastName }}
        </span>
        <span class="is-block" v-if="registrationData.billingAddress.street">{{ registrationData.billingAddress.street }}</span>
        <span class="is-block" v-if="registrationData.billingAddress.zipCode || registrationData.billingAddress.city">
            {{ registrationData.billingAddress.zipCode }} {{ registrationData.billingAddress.city }}
        </span>
        <br>
        <span class="is-block" v-if="registrationData.billingAddress.phone">{{ registrationData.billingAddress.phone }}</span>
        <span class="is-block" v-if="registrationData.billingAddress.email">{{ registrationData.billingAddress.email }}</span>
        <span class="is-block" v-if="registrationData.billingAddress.dateOfBirth">
            Geburtsdatum: {{ (new Date(registrationData.billingAddress.dateOfBirth)).toLocaleDateString('de-DE') }}
        </span>
    </div>
    <div>
        <p class="subheadline">Zahlweise</p>
        <span class="is-block" v-if="paymentMethodName">{{ paymentMethodName }}</span>
    </div>
    <div v-if="showBankAccountInfo">
        <p class="subheadline">Bankverbindung</p>
        <span class="is-block" v-if="registrationData.payment.bankAccountOwner">{{ registrationData.payment.bankAccountOwner }}</span>
        <span class="is-block" v-if="registrationData.payment.bankName">{{ registrationData.payment.bankName }}</span>
        <span class="is-block" v-if="registrationData.payment.bankBic">{{ registrationData.payment.bankBic }}</span>
        <span class="is-block" v-if="registrationData.payment.bankIban">{{ registrationData.payment.bankIban }}</span>
    </div>
    <div class="subnote-send" v-if="registrationData.billingViaEmail">
        <span class="is-block">Ich möchte meine Rechnungen per E-Mail erhalten.</span>
    </div>
    <div class="subnote-send">
        <Button text="Senden" @click="confirmData" :disabled="!confirmButtonEnabled"></Button>
    </div>
</template>

<script>
import Button from '@/views/components/ui/Button'
import { Destination, RegistrationRole, getNameForPaymentType, paymentMethodRequiresBankAccountInfo } from '@/utils/registration'
import {format} from "date-fns";

export default {
    name: 'RegisterStep2ConfirmData',
    components: {
        Button,
    },
    props: {
        registrationData: Object,
        destination: {
            type: String,
            required: true,
            validator: value => {
                return Object.values(Destination).includes(value)
            }
        },
        registrationFormOptions: {
            type: [Object, null],
            default: null,
        },
        confirmButtonEnabled: Boolean
    },
    computed: {
        institutionName() {
            if (this.registrationFormOptions === null) {
                return ''
            }

            return this.registrationFormOptions.institutionName || ''
        },
        showChild() {
            return (this.destination === Destination.Kita || this.destination === Destination.School)
                && this.registrationData.role === RegistrationRole.Child
        },
        showParticipant() {
            return this.destination === Destination.Work && this.registrationFormOptions.institutionName
        },
        showDeliveryAddress() {
            return this.registrationData.deliveryAddress
                && (this.registrationData.deliveryAddress.salutation
                    || this.registrationData.deliveryAddress.company
                    || this.registrationData.deliveryAddress.firstName
                    || this.registrationData.deliveryAddress.lastName
                    || this.registrationData.deliveryAddress.street
                    || this.registrationData.deliveryAddress.zipCode
                    || this.registrationData.deliveryAddress.city
                    || this.registrationData.deliveryAddress.phone
                    || this.registrationData.deliveryAddress.email
                    || this.registrationData.deliveryAddress.deliveryInfo
                    || this.registrationData.deliveryAddress.dateOfBirth)
        },
        showBillingAddress() {
            return this.registrationData.billingAddress
                && (this.registrationData.billingAddress.salutation
                    || this.registrationData.billingAddress.company
                    || this.registrationData.billingAddress.firstName
                    || this.registrationData.billingAddress.lastName
                    || this.registrationData.billingAddress.street
                    || this.registrationData.billingAddress.zipCode
                    || this.registrationData.billingAddress.city
                    || this.registrationData.billingAddress.phone
                    || this.registrationData.billingAddress.email
                    || this.registrationData.billingAddress.dateOfBirth)
        },
        paymentMethodName() {
            if (!this.registrationData.payment || !this.registrationData.payment.method) {
                return ''
            }
            return getNameForPaymentType(this.registrationData.payment.method)
        },
        showBankAccountInfo() {
            if (!this.registrationData.payment || !this.registrationData.payment.method) {
                return false
            }
            return paymentMethodRequiresBankAccountInfo(this.registrationData.payment.method)
        },
        formattedChildDateStart() {
            if (!this.registrationData.child || !this.registrationData.child.dateStart) {
                return ''
            }
            const date = new Date(this.registrationData.child.dateStart)
            return format(date, 'dd.MM.yyyy')
        }
    },
    methods: {
        async confirmData() {
            this.$emit('dataConfirmed')
        }
    },
    emits: ['dataConfirmed'],
}
</script>
